import React, { useState, SyntheticEvent } from 'react'
import { Classes, InputGroup, Intent, FormGroup } from '@blueprintjs/core'
import { Flex, Confirmation, Button, Details, Toaster } from '@ui'
import api, { IWorkplaceGroupTableRecord, FormErrors } from '@api'
import { ApolloError } from 'apollo-boost'

export interface FormWorkplaceGroup {
  id?: string
  name: string
}

const initWorkplaceGroup: FormWorkplaceGroup = {
  name: '',
}

interface RequiredField {
  key: 'name'
  label: string
}

const requiredFields: RequiredField[] = [{ key: 'name', label: 'Name' }]

function isValid(workplaceGroup: FormWorkplaceGroup) {
  let valid = true
  requiredFields.forEach(({ key }) => {
    if (valid) {
      valid = !!workplaceGroup[key]
    }
  })

  return valid
}

type WorkplaceGroupFormErrors = FormErrors<FormWorkplaceGroup>

export default function WorkplaceGroupForm({
  dirty,
  setDirty,
  onCancel,
  workplaceGroup: initState = initWorkplaceGroup,
  onSubmit,
}: {
  workplaceGroup?: FormWorkplaceGroup
  setDirty: (value: boolean) => void
  dirty: boolean
  onSubmit: (newWorkplaceGroup: IWorkplaceGroupTableRecord) => void
  onCancel: () => void
}) {
  const [workplaceGroup, setWorkplaceGroup] = useState<FormWorkplaceGroup>(initState)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<WorkplaceGroupFormErrors>({})

  const onTextInputChange = ({
    currentTarget: { name, value },
  }: SyntheticEvent<HTMLInputElement>) => {
    setDirty(true)
    setWorkplaceGroup({
      ...workplaceGroup,
      [name]: value,
    })
  }

  const handleErrors = ({ kind, graphQLErrors }: any) => {
    setLoading(false)

    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: WorkplaceGroupFormErrors = graphQLErrors.reduce(
        (result: WorkplaceGroupFormErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const highlightErrors = () => {
    const foundErrors: WorkplaceGroupFormErrors = {}
    requiredFields.forEach((field) => {
      if (!workplaceGroup[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }
    })
    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const columns = [
    [
      {
        label: 'Name',
        required: true,
        formEl: (
          <FormGroup helperText={errors.name} intent={errors.name ? Intent.DANGER : undefined}>
            <InputGroup
              autoFocus
              placeholder="Name of new workplace group"
              name="name"
              intent={errors.name ? Intent.DANGER : undefined}
              value={workplaceGroup.name}
              onChange={onTextInputChange}
            />
          </FormGroup>
        ),
      },
    ],
  ]

  return (
    <div style={{ paddingTop: 20 }} className={Classes.DIALOG_BODY}>
      <Details
        onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
          e.preventDefault()
          if (!isValid(workplaceGroup)) {
            highlightErrors()
            return
          }
          setLoading(true)

          if (workplaceGroup?.id) {
            api.workplaceGroup
              .updateName({
                id: workplaceGroup.id,
                name: workplaceGroup.name,
              })
              .then(() => onSubmit(workplaceGroup as IWorkplaceGroupTableRecord))
              .then(() =>
                Toaster.success({
                  message: 'Workplace group updated',
                })
              )
              .catch((error: ApolloError) => {
                handleErrors(error)
                Toaster.fail({
                  error,
                })
              })
          } else {
            api.workplaceGroup
              .create({
                name: workplaceGroup.name,
              })
              .then((wpg) => onSubmit(wpg as IWorkplaceGroupTableRecord))
              .then(() =>
                Toaster.success({
                  message: 'Workplace group created',
                })
              )
              .catch((error: ApolloError) => {
                handleErrors(error)
                Toaster.fail({
                  error,
                })
              })
          }
        }}
      >
        <Details.Columns>
          {columns.map((rows, i) => (
            <Details.Column key={i} style={i === 0 ? { width: 582 } : {}}>
              {rows.map(({ label, required, formEl }) => (
                <Details.Row key={label} label={label} required={required}>
                  {formEl}
                </Details.Row>
              ))}
            </Details.Column>
          ))}
        </Details.Columns>
        <Details.Footer style={{ paddingTop: 10 }}>
          <Flex>
            <div style={{ marginRight: 8 }}>
              {dirty ? (
                <Confirmation text="Discard unsaved changes?" onConfirm={onCancel}>
                  <Button type="button" text="Cancel" />
                </Confirmation>
              ) : (
                <Button type="button" text="Cancel" onClick={onCancel} />
              )}
            </div>
            <Button
              type="submit"
              data-button={workplaceGroup?.id ? 'update_workplace_group' : 'create_workplace_group'}
              disabled={!dirty}
              loading={loading}
              intent={Intent.PRIMARY}
              text={workplaceGroup?.id ? 'Update' : 'Create'}
            />
          </Flex>
        </Details.Footer>
      </Details>
    </div>
  )
}
